<template>
  <div>
    <TransitionRoot as="template" :show="!!props.open">
      <Dialog as="div" class="relative z-30" @close="close">
        <div class="fixed inset-0" />

        <div class="fixed inset-0 overflow-hidden">
          <div class="absolute inset-0 overflow-hidden">
            <div
              class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full sm:pl-10"
            >
              <TransitionChild
                as="template"
                enter="transform transition ease-in-out duration-200"
                enter-from="translate-x-full"
                enter-to="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leave-from="translate-x-0"
                leave-to="translate-x-full"
              >
                <DialogPanel class="pointer-events-auto w-screen max-w-md">
                  <div
                    id="scroll-container"
                    class="flex h-full flex-col overflow-y-scroll bg-white shadow-xl"
                  >
                    <div class="fixed bg-white max-w-md w-full z-50">
                      <div
                        class="p-4 sm:p-6 border-b border-b-gray-200 bg-white"
                      >
                        <div class="flex items-center justify-between">
                          <div class="flex sm:hidden">
                            <h1
                              v-if="!props.document"
                              class="font-semibold text-xl"
                            >
                              {{ t("admin.settings.documents.add_document") }}
                            </h1>
                            <h1 v-else class="font-semibold text-xl">
                              {{
                                t("admin.settings.documents.update_document")
                              }}
                            </h1>
                          </div>
                          <div v-if="!props.document">
                            <primary-button
                              v-if="canUpdate"
                              class="hidden sm:flex"
                              :label="
                                t('admin.settings.documents.add_document')
                              "
                              @click="handleAddDocument"
                            />
                          </div>
                          <div v-else class="flex space-x-2 hidden sm:flex">
                            <primary-button
                              v-if="canUpdate"
                              :label="
                                t('admin.settings.documents.update_document')
                              "
                              @click="handleUpdateDocument"
                            />
                            <div
                              class="flex flex-row gap-2 items-center mr-4"
                              v-else
                            >
                              <span class="font-medium text-xl">{{
                                t("admin.settings.details")
                              }}</span>
                              <Tooltip :content="t('admin.settings.read_only')">
                                <Eye class="w-5 text-gray-600" />
                              </Tooltip>
                            </div>
                            <button
                              v-if="canDelete"
                              @click="handleDeleteDocument"
                              type="button"
                              class="rounded-md border border-red-600 bg-white px-2.5 py-2 text-base font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none sm:w-auto sm:text-sm disabled:opacity-50"
                            >
                              <TrashOne class="w-5 text-red-600" />
                            </button>
                          </div>
                          <div class="ml-3 flex h-7 items-center">
                            <button
                              type="button"
                              class="rounded-sm bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                              @click="close"
                            >
                              <span class="sr-only">Close panel</span>
                              <XMarkIcon class="h-6 w-6" aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div
                      class="flex flex-col space-y-8 mt-24 sm:mt-28 flex-1 px-4 sm:px-6"
                    >
                      <div class="flex flex-col space-y-3">
                        <Errors />

                        <div>
                          <span class="font-medium">
                            {{ t("admin.settings.documents.space.title") }}
                          </span>
                          <p class="text-gray-500 text-sm font-medium">
                            {{
                              t("admin.settings.documents.space.description")
                            }}
                          </p>
                        </div>
                        <LocationDropdown
                          disable-ungrouped
                          disable-tenants
                          :tenants="filteredTenants"
                          :model-value="selectedLocation"
                          selector-style="dropdown"
                          @update:model-value="handleLocationSelected"
                          :disabled="!canUpdate"
                        />
                      </div>

                      <div class="flex flex-col space-y-3">
                        <div>
                          <span class="font-medium">
                            {{ t("admin.settings.documents.title.title") }}
                          </span>
                          <p class="text-gray-500 text-sm font-medium">
                            {{
                              t("admin.settings.documents.title.description")
                            }}
                          </p>
                        </div>
                        <input
                          v-model="currentSelection.title"
                          type="text"
                          name="title"
                          id="title"
                          class="block w-full rounded-sm border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm"
                          :class="{
                            '!cursor-not-allowed opacity-60': !canUpdate,
                          }"
                          :disabled="!canUpdate"
                        />
                      </div>

                      <div class="flex flex-col space-y-3">
                        <div>
                          <span class="font-medium">
                            {{
                              t("admin.settings.documents.description.title")
                            }}
                          </span>
                          <p class="text-gray-500 text-sm font-medium">
                            {{
                              t(
                                "admin.settings.documents.description.description"
                              )
                            }}
                          </p>
                        </div>
                        <input
                          v-model="currentSelection.description"
                          type="text"
                          name="description"
                          id="description"
                          class="block w-full rounded-sm border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm"
                          :class="{
                            '!cursor-not-allowed opacity-60': !canUpdate,
                          }"
                          placeholder=""
                          :disabled="!canUpdate"
                        />
                      </div>

                      <div class="flex flex-col space-y-3">
                        <input
                          type="file"
                          ref="attachments"
                          @input="handleAttachmentsSelected"
                          multiple
                          hidden
                        />

                        <div>
                          <span class="font-medium">
                            {{
                              t("admin.settings.documents.attachments.title")
                            }}
                          </span>
                          <p class="text-gray-500 text-sm font-medium">
                            {{
                              t(
                                "admin.settings.documents.attachments.description"
                              )
                            }}
                          </p>
                        </div>
                        <button
                          :disabled="
                            isUploadingAttachment ||
                            !currentSelection.space_id ||
                            !canUpdate
                          "
                          @click="handleOpenAttachmentSelector"
                          :class="{
                            '!cursor-not-allowed opacity-60': !canUpdate,
                          }"
                          class="flex items-center space-x-2 w-full rounded-sm border py-2 px-3 border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500 sm:text-sm"
                        >
                          <PlusIcon
                            class="w-4 h-4 text-gray-700"
                            v-if="!isUploadingAttachment"
                          />
                          <LoadingSpinner
                            class="w-4 h-4 text-gray-700"
                            v-else
                          />
                          <span>{{
                            t("admin.settings.documents.upload.title")
                          }}</span>
                        </button>

                        <div
                          v-if="props.document?.documents"
                          class="flex flex-col space-y-2"
                        >
                          <div
                            class="flex justify-between space-x-2 items-center bg-white border border-gray-200 rounded-md px-2 py-1 hover:cursor-pointer"
                            v-for="preview in currentSelection.documents"
                            :key="preview.id"
                            @click="downloadFile(preview.urls.file)"
                          >
                            <img
                              v-if="
                                [
                                  'image/png',
                                  'image/jpg',
                                  'image/jpeg',
                                ].includes(preview.mime_type)
                              "
                              class="w-12 h-12 object-scale-down"
                              :src="preview.urls.thumbnail"
                              :alt="preview.name"
                            />
                            <div v-else>
                              <div
                                class="w-12 h-12 flex items-center justify-center"
                              >
                                <FileFive class="w-5 h-5" />
                              </div>
                            </div>

                            <div
                              class="flex w-full flex-col text-sm overflow-hidden pr-12"
                            >
                              <span class="truncate text-ellipsis">{{
                                preview.name
                              }}</span>
                            </div>
                            <div
                              v-if="canUpdate"
                              @click.stop="
                                handleRemoveUploadedAttachment(preview.id)
                              "
                            >
                              <XMarkIcon
                                class="h-5 w-5 cursor-pointer text-gray-400 hover:text-gray-500"
                                aria-hidden="true"
                              />
                            </div>
                          </div>
                        </div>

                        <div
                          class="flex flex-col space-y-2"
                          v-if="previews.length > 0"
                        >
                          <div
                            class="flex justify-between space-x-2 items-center bg-white border border-gray-200 rounded-md px-2 py-1"
                            v-for="preview in previews"
                            :key="preview.id"
                          >
                            <img
                              v-if="preview.src"
                              class="w-12 h-12 object-scale-down"
                              :src="preview.src"
                              :alt="preview.name"
                            />
                            <div v-else>
                              <div
                                class="w-12 h-12 flex items-center justify-center"
                              >
                                <FileFive class="w-5 h-5" />
                              </div>
                            </div>

                            <div
                              class="flex w-full flex-col text-sm overflow-hidden pr-12"
                            >
                              <span class="truncate text-ellipsis">{{
                                preview.name
                              }}</span>
                              <span class="text-gray-500">{{
                                formatBytes(preview.size)
                              }}</span>
                            </div>
                            <div v-if="canUpdate">
                              <XMarkIcon
                                class="h-5 w-5 cursor-pointer text-gray-400 hover:text-gray-500"
                                aria-hidden="true"
                                @click="handleRemoveAttachment(preview.id)"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <hr />

                      <SwitchGroup as="div" class="flex items-center z-0">
                        <Switch
                          v-model="currentSelection.has_expiration_date"
                          :class="[
                            currentSelection.has_expiration_date
                              ? 'bg-gray-600'
                              : 'bg-gray-200',
                            'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none',
                            {
                              '!cursor-not-allowed opacity-60 !cursor-default':
                                !canUpdate,
                            },
                          ]"
                          :disabled="!canUpdate"
                        >
                          <span
                            aria-hidden="true"
                            :class="[
                              currentSelection.has_expiration_date
                                ? 'translate-x-5'
                                : 'translate-x-0',
                              'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                            ]"
                          />
                        </Switch>
                        <SwitchLabel as="span" class="ml-3">
                          <span class="font-medium text-gray-900">
                            {{
                              t(
                                "admin.settings.documents.enable_expiration_reminder"
                              )
                            }}
                          </span>
                        </SwitchLabel>
                      </SwitchGroup>

                      <div
                        class="flex flex-col space-y-3"
                        v-if="currentSelection.has_expiration_date"
                      >
                        <div>
                          <span class="font-medium">
                            {{
                              t(
                                "admin.settings.documents.expiration_date.title"
                              )
                            }}
                          </span>
                          <p class="text-gray-500 text-sm font-medium">
                            {{
                              t(
                                "admin.settings.documents.expiration_date.description"
                              )
                            }}
                          </p>
                        </div>
                        <date-input-selector
                          @change="handleExpirationDateChanged"
                          :schedule-date="currentSelection.expiration_date"
                          :disabled="!canUpdate"
                        />
                      </div>

                      <div
                        class="flex flex-col space-y-3"
                        v-if="currentSelection.has_expiration_date"
                      >
                        <div>
                          <span class="font-medium">
                            {{
                              t(
                                "admin.settings.documents.notification_date.title"
                              )
                            }}
                          </span>
                          <p class="text-gray-500 text-sm font-medium">
                            {{
                              t(
                                "admin.settings.documents.notification_date.description"
                              )
                            }}
                          </p>
                        </div>
                        <date-input-selector
                          @change="handleNotificationDateChanged"
                          :schedule-date="currentSelection.reminder_date"
                          :disabled="!canUpdate"
                        />
                      </div>
                    </div>

                    <div class="px-4 py-4 w-full">
                      <div v-if="!props.document">
                        <primary-button
                          v-if="canUpdate"
                          class="sm:hidden flex w-full"
                          :label="t('admin.settings.documents.add_document')"
                          @click="handleAddDocument"
                        />
                      </div>
                      <div v-else class="flex flex-col space-y-2 sm:hidden">
                        <primary-button
                          v-if="canUpdate"
                          class="w-full"
                          :label="t('admin.settings.documents.update_document')"
                          @click="handleUpdateDocument"
                        />
                        <button
                          v-if="canDelete"
                          @click="handleDeleteDocument"
                          type="button"
                          class="rounded-md border border-red-600 text-red-600 bg-white px-2.5 py-2 text-base font-medium shadow-sm hover:text-gray-500 focus:outline-none sm:w-auto sm:text-sm disabled:opacity-50"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </div>
</template>

<script setup>
import {
  computed,
  defineEmits,
  defineProps,
  inject,
  reactive,
  ref,
  watch,
} from "vue";
import {
  Dialog,
  DialogPanel,
  Switch,
  SwitchGroup,
  SwitchLabel,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";
import { XMarkIcon } from "@heroicons/vue/24/outline";
import PrimaryButton from "@/Tailwind/Components/Buttons/PrimaryButton";
import TrashOne from "@/Tailwind/Icon/TrashOne";
import DateInputSelector from "@/Tailwind/Components/Input/DateInputSelector.vue";
import { includeHours } from "@/Tailwind/Helpers/time";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import { useUTC } from "@/Tailwind/Mixins/useUTC";
import PlusIcon from "@/Tailwind/Icon/PlusIcon.vue";
import axios from "axios";
import { uniqueId } from "lodash";
import FileFive from "@/Tailwind/Icon/FileFive.vue";
import { formatBytes } from "@/Tailwind/Helpers/size";
import LoadingSpinner from "@/Tailwind/Components/LoadingSpinner.vue";
import Eye from "@/Tailwind/Icon/Eye.vue";
import Tooltip from "@/Tailwind/Components/Tooltip.vue";

import { useI18n } from "vue-i18n";
import Errors from "@/Tailwind/Components/Errors.vue";
import { usePage } from "@inertiajs/vue3";
import { useWebView } from "@/Tailwind/Mixins/useWebView";
import { mobileSatisfiesVersion } from "@/Tailwind/Helpers/version";
import LocationDropdown from "@/Tailwind/Components/Input/LocationDropdown.vue";
const { t } = useI18n();

const emit = defineEmits(["close", "close-and-refresh"]);
const props = defineProps({
  open: Boolean,
  document: Object,
});

const { hasPermission, tenantsWithUserPermission, permissions } =
  inject("permissions");

const filteredTenants = computed(() =>
  usePage().props.tenants.filter((tenant) => {
    // Check if other tenants have the correct permission
    // Changing a task from one tenant to another requires the create permission
    const otherTenants =
      tenantsWithUserPermission(permissions.createDocument) ?? [];

    // Include the owner tenant if the user has the correct permission
    const currentTenant = hasPermission(
      props.document ? permissions.updateDocument : permissions.createDocuemnt,
      props.document?.tenant.id
    )
      ? props.document?.tenant.id
      : null;

    return [...otherTenants, currentTenant].includes(tenant.id);
  })
);

const canUpdate = computed(() =>
  props.document
    ? hasPermission(permissions.updateDocument, props.document.tenant.id)
    : hasPermission(permissions.createDocument, null)
);

const canDelete = computed(() =>
  props.document
    ? hasPermission(permissions.deleteDocument, props.document.tenant.id)
    : false
);

watch(
  () => props.document,
  () => {
    if (!props.document) {
      return;
    }

    currentSelection.space_id = props.document?.space?.id;
    currentSelection.tenant_id = props.document?.space?.tenant_id;
    currentSelection.has_expiration_date = !!props.document?.expiration_date;
    currentSelection.title = props.document?.title;
    currentSelection.description = props.document?.description;
    currentSelection.expiration_date = props.document?.expiration_date
      ? fromUTC(props.document.expiration_date)
      : null;
    currentSelection.reminder_date = props.document?.reminder_date
      ? fromUTC(props.document.reminder_date)
      : null;
    currentSelection.documents = props.document?.documents || [];
    selectedLocation.value = props.document?.space;
  }
);

const isUploadingAttachment = ref(false);
const attachments = ref(null);
const { toUTC, fromUTC } = useUTC();

const currentSelection = reactive({
  space_id: props.document?.space?.id,
  tenant_id: props.document?.space?.tenant_id,
  has_expiration_date: !!props.document?.expiration_date,
  title: props.document?.title,
  description: props.document?.description,
  expiration_date: props.document?.expiration_date
    ? fromUTC(props.document.expiration_date)
    : null,
  reminder_date: props.document?.reminder_date
    ? fromUTC(props.document.reminder_date)
    : null,
  documents: props.document?.documents || [],
});

function reset() {
  currentSelection.space_id = null;
  currentSelection.tenant_id = null;
  currentSelection.has_expiration_date = false;
  currentSelection.title = null;
  currentSelection.description = null;
  currentSelection.expiration_date = null;
  currentSelection.reminder_date = null;
  currentSelection.documents = [];
  selectedAttachments.value = [];
  selectedLocation.value = null;
}

const { runningInWebView, openURL } = useWebView();
const downloadFile = (url) => {
  if (!runningInWebView()) {
    window.open(url, "_blank");
    return;
  }

  if (mobileSatisfiesVersion("2.2.0")) {
    openURL(url);
    return;
  }

  // Fallback for outdated mobile apps.
  window.open(url, "_blank");
};

function handleRemoveUploadedAttachment(id) {
  currentSelection.documents = currentSelection.documents.filter(
    (d) => d.id !== id
  );
}

const selectedAttachments = ref([]);
const previews = computed(() => {
  return selectedAttachments.value.map((attachment) => ({
    id: attachment.id,
    name: attachment.data.name,
    size: attachment.data.size,
    src: ["image/png", "image/jpg", "image/jpeg"].includes(attachment.data.type)
      ? URL.createObjectURL(attachment.data)
      : null,
  }));
});

function handleRemoveAttachment(id) {
  selectedAttachments.value = selectedAttachments.value.filter(
    (attachment) => attachment.id !== id
  );
}

function handleOpenAttachmentSelector() {
  attachments.value.click();
}

async function handleAttachmentsSelected(e) {
  const files = e.target.files;

  isUploadingAttachment.value = true;

  for (const file of files) {
    const document = new FormData();
    document.append("file", file, file.name);

    const { data: response } = await axios.post(
      route("api.documents.store", [currentSelection.tenant_id]),
      document
    );

    selectedAttachments.value = [
      ...selectedAttachments.value,
      {
        id: uniqueId(),
        documentId: response.data.id,
        data: file,
      },
    ];
  }

  isUploadingAttachment.value = false;

  // Allow selection of the same file again.
  e.target.value = null;
}

const selectedLocation = ref();
function handleLocationSelected(location) {
  if (!location) {
    return;
  }

  selectedLocation.value = location;

  currentSelection.space_id = location.id;
  currentSelection.tenant_id = location.tenant;
}

function handleExpirationDateChanged(date) {
  if (date) {
    currentSelection.expiration_date = toUTC(includeHours(date).toISOString());
  }
}

function handleNotificationDateChanged(date) {
  if (date) {
    currentSelection.reminder_date = toUTC(includeHours(date).toISOString());
  }
}

function handleAddDocument() {
  router.post(
    route("settings.organisation.notes.store"),
    {
      ...currentSelection,
      documents: selectedAttachments.value.map((attachment) => ({
        id: attachment.documentId,
      })),
    },
    {
      onSuccess: () => {
        closeRefresh();
        reset();
      },
    }
  );
}

function handleUpdateDocument() {
  router.put(
    route("settings.organisation.notes.update", {
      note: props.document.id,
    }),
    {
      ...currentSelection,
      expiration_date: currentSelection.has_expiration_date
        ? toUTC(includeHours(currentSelection.expiration_date).toISOString())
        : null,
      reminder_date: currentSelection.has_expiration_date
        ? toUTC(includeHours(currentSelection.reminder_date).toISOString())
        : null,
      documents: [
        ...currentSelection.documents.map((d) => ({ id: d.id })),
        ...selectedAttachments.value.map((attachment) => ({
          id: attachment.documentId,
        })),
      ],
    },
    {
      onSuccess: () => {
        closeRefresh();
        reset();
      },
    }
  );
}

function handleDeleteDocument() {
  router.delete(
    route("settings.organisation.notes.destroy", {
      note: props.document.id,
    }),
    {
      onSuccess: () => {
        closeRefresh();
        reset();
      },
    }
  );
}

const close = () => {
  reset();
  emit("close");
};
const closeRefresh = () => emit("close-and-refresh");
</script>
